import { EntityDetail } from '@/components/data/Benchmarking/utils/getEntityDetails'
import { BenchmarkingItemWithUnit, BenchmarkingReportSettings } from '@/hooks'
import { SQUARE_FOOTAGE_UNIT } from '@/utils/constants'
import { formatDateForTable } from '@/utils/formatDateForTable'
import { numberWithCommas } from '@/utils/numberWithCommas'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useColumnHeaderText } from './useColumnHeaderText'

export const useColumns = (
    data: BenchmarkingItemWithUnit[],
    entitiesMap: Map<string, EntityDetail>,
    squareFootages: Record<string, number>,
    reportSettings: BenchmarkingReportSettings,
    labels: {
        currentYear: string
        baselineYear: string
        previousYear: string
    }
) => {
    const { t } = useTranslation('translations')
    const headerText = useColumnHeaderText(data, reportSettings)

    return useMemo(
        () =>
            getColumns({
                t,
                headerText,
                data,
                entitiesMap,
                squareFootages,
                reportSettings,
                labels,
            }),
        [
            t,
            headerText,
            data,
            entitiesMap,
            squareFootages,
            reportSettings,
            labels,
        ]
    )
}

const getColumns = (props: {
    t: (key: string) => string
    headerText: string
    data: BenchmarkingItemWithUnit[]
    entitiesMap: Map<string, EntityDetail>
    squareFootages: Record<string, number>
    reportSettings: BenchmarkingReportSettings
    labels: { currentYear: string; baselineYear: string; previousYear: string }
}) => {
    const display = props.reportSettings.display
    const shouldShowSquareFootage =
        display === 'facility' || display === 'group'

    let nestedColumns: ColumnDef<unknown>[] = []
    if (props.reportSettings.analysisType === 'trend') {
        nestedColumns = createColumnsForTrendDisplayType(props.data)
    } else {
        nestedColumns = createColumnsForAverageAndTotalsDisplayType(
            props.reportSettings,
            props.labels
        )
    }

    const columns: ColumnDef<unknown>[] = [
        {
            id: display,
            accessorKey: 'entityId',
            header: props.t(
                `analytics.benchmarking.columnHeaders.firstColumn.${props.reportSettings.display}`
            ),
            sortingFn: 'alphanumeric',
            cell: ({ getValue }) => {
                const entityId = getValue() as string
                const entityName =
                    props.entitiesMap.get(entityId)?.name || entityId
                return (
                    <div
                        className="overflow-hidden text-ellipsis whitespace-nowrap"
                        data-testid={`row-${entityId}`}
                        title={entityId}
                    >
                        {entityName}
                        {shouldShowSquareFootage && (
                            <div>
                                (
                                {`${numberWithCommas(props.squareFootages[entityId] || 0, 0)} ${SQUARE_FOOTAGE_UNIT}`}
                                )
                            </div>
                        )}
                    </div>
                )
            },
        },
        {
            header: props.headerText,
            id: props.reportSettings.basis,
            columns: nestedColumns,
        },
    ]

    return columns
}

const createColumnsForTrendDisplayType = (data: BenchmarkingItemWithUnit[]) => {
    const monthsSet = new Set<string>()
    const dateColumns: ColumnDef<unknown>[] = []

    for (const item of data) {
        if (!item.billDate) {
            continue
        }

        const date = formatDateForTable(item.billDate)
        monthsSet.add(date)
    }

    for (const label of Array.from(monthsSet)) {
        dateColumns.push({
            id: label,
            accessorKey: label,
            header: () => (
                <div data-testid={`column-header-${label}`}>{label}</div>
            ),
            cell: numericCell,
        })
    }

    return dateColumns
}

const createColumnsForAverageAndTotalsDisplayType = (
    reportSettings: BenchmarkingReportSettings,
    labels: { currentYear: string; baselineYear: string; previousYear: string }
) => {
    const { comparisonPeriod } = reportSettings
    const dateColumns: ColumnDef<unknown>[] = []
    let label = ''

    switch (comparisonPeriod) {
        case 'baselineYear':
            label = labels.baselineYear
            break
        case 'previousYear':
            label = labels.previousYear
            break
        default:
            label = ''
    }

    if (label) {
        dateColumns.push({
            id: label,
            accessorKey: label,
            header: () => (
                <div data-testid={`column-header-${label}`}>{label}</div>
            ),
            cell: numericCell,
        })
    }

    dateColumns.push({
        id: labels.currentYear,
        accessorKey: labels.currentYear,
        header: () => (
            <div data-testid={`column-header-${labels.currentYear}`}>
                {labels.currentYear}
            </div>
        ),
        cell: numericCell,
    })

    return dateColumns
}

const numericCell = ({ getValue }: { getValue: () => unknown }) => {
    const value = getValue() as number | undefined

    if (value === undefined) {
        return '—'
    }

    return numberWithCommas(value, 2)
}
